import { AnswersPage, CommandResult, PublicSurveyReportLink, SegmentReport, Survey, SurveyAnswerGraphData, LanguageCode, SurveyReport, SurveySegment, SurveySegmentation, SurveySegmentFilterAlias, SurveyTemplateView, rng, SharedSurveyReport, SurveyQuestionConfig, SurveyInvitationFile, LocalizedSurveyText, CommentData } from '../amorg-common'
import { apiPath } from "./../amorg-common";
import { commandAuthWrapper, queryAuthWrapper, postQueryAuthWrapper, rawCommandAuthWrapper } from "./serverApiHelpers";


export const surveysPath = `${apiPath}/surveys`;

export async function uploadSurveyInvitationFile(surveyId:string, id:string, fileHandle:Blob)
{
    const formData = new FormData();
    formData.append('uploaded_file', fileHandle);
    
    return await rawCommandAuthWrapper(`${surveysPath}/${surveyId}/answerFileImport/${id}`, 'POST', formData, {});
}

export async function sendSurveyInvitationFile(surveyId:string, fileId:string, emailSubject:string, emailBody:string )
{
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/sendAnswerFileImport/${fileId}`, 'POST', {emailSubject, emailBody});
}

export async function getLastestUploadFile(surveyId: string):Promise<SurveyInvitationFile | null> {
    return await queryAuthWrapper(`${surveysPath}/${surveyId}/answerFileImport`);
}

export async function generateSurveyAnswers(surveyId: string, answerCount: number): Promise<CommandResult> {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/testanswers`, 'PUT', { answerCount });
}

export async function moveSurveySegment(surveyId: string, segmentations: SurveySegmentation[], selectedSegmentationIndex: number, fromValueIndex: number, toValueIndex: number): Promise<CommandResult> {

    function moveValue(fromIndex:number, toIndex:number, values:string[])
    {
        const val = values.splice(fromIndex, 1)[0];
        if(toIndex === values.length)
            values.push(val);
        else
            values.splice(toIndex, 0, val);
    }

    const segmentation = segmentations[selectedSegmentationIndex];
    if(fromValueIndex>=0 && fromValueIndex<segmentation.values.length &&
        toValueIndex>=0 && toValueIndex<segmentation.values.length &&
        fromValueIndex !== toValueIndex)
    {
        moveValue(fromValueIndex, toValueIndex, segmentation.values);
        for(const t of segmentation.translations)
        {
            moveValue(fromValueIndex, toValueIndex, t.values);
        }
        return await updateSurveySegmentation(surveyId, selectedSegmentationIndex, selectedSegmentationIndex, segmentation);
    }
    else
        return CommandResult.succeeds();
}


export const createSurveySegmentation = async (surveyId:string, segmentation:SurveySegmentation): Promise<CommandResult> => {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/segmentations`, 'POST',
        { segmentation });
};
export const updateSurveySegmentation = async (surveyId:string, index:number, toIndex:number, segmentation:SurveySegmentation): Promise<CommandResult> => {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/segmentations/${index}`, 'PUT',
        { toIndex, segmentation });
};
export const deleteSurveySegmentation = async (surveyId:string, index:number): Promise<CommandResult> => {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/segmentations/${index}`, 'DELETE');
};


// export const setSurveySegmentations = async (id: string, segmentations: SurveySegmentation[]): Promise<CommandResult> => {
//     return await commandAuthWrapper(`${surveysPath}/${id}/segmentations`, 'PUT',
//         { segmentations });
// };

export const getSurvey = async (surveyId: string): Promise<Survey> => {
    const result = await queryAuthWrapper(`${surveysPath}/${surveyId}`);
    return { ...result, start: new Date(result.start), end: new Date(result.end) };
}

export const getSurveyAnswersPage = async (surveyId: string, 
    searchTerm:string, segmentFilter:SurveySegment[][], completion:'all'|'complete'|'incomplete',
    page:number, pageSize:number, sortBy:'started' | 'finished' | 'email', sortAscending:boolean): Promise<AnswersPage> => {

    const query = 
        `?searchterm=${searchTerm}` + 
        `&segmentfilter=${JSON.stringify(segmentFilter)}` + 
        `&completion=${completion}` + 
        `&page=${page}` +
        `&pagesize=${pageSize}` +
        `&sortby=${sortBy}` +
        `&ascending=${sortAscending}`;

    const result = await queryAuthWrapper(`${surveysPath}/${surveyId}/answers${query}`) as AnswersPage;
    if (result.answers.length > 0)
        result.answers = result.answers.map(a => {
            return {
                ...a,
                started: a.started === null ? null : new Date(a.started),
                finished: a.finished === null ? null : new Date(a.finished)
            }
        });
    return result;
}

export async function getSurveyAnswerGraphData(surveyId: string, periodStart:Date): Promise<SurveyAnswerGraphData>
{
    const result = await queryAuthWrapper(`${surveysPath}/${surveyId}/answers/graphdata?periodstart=${periodStart}`) as SurveyAnswerGraphData;
    for(const x of result.stats){
        x.date = new Date(x.date);
    }
    return result;
}

export const getSurveyReport = async (surveyId: string): Promise<SurveyReport> =>
    await queryAuthWrapper(`${surveysPath}/${surveyId}/report`);

export const getSurveySegmentReport = async (surveyId: string): Promise<SegmentReport[]> =>
    await queryAuthWrapper(`${surveysPath}/${surveyId}/segmentreport`);

export const getSurveyComments = async (siteId: string, surveyId: string, segmentFilter: SurveySegment[][], themeIndex: number, questionIndex: number): Promise<CommentData[]> => {
    return await postQueryAuthWrapper(`${surveysPath}/${siteId}.${surveyId}/report/comments`,
        {
            themeIndex,
            questionIndex,
            segmentFilter
        });
}

export async function createSurveyReportLink(surveyId: string, linkId: string, segmentFilter: SurveySegment[][]): Promise<CommandResult> {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/report/links`, 'POST',
        { linkId, segmentFilter });
}

export async function deleteSurveyReportLink(linkId: string): Promise<CommandResult> {
    return await commandAuthWrapper(`${surveysPath}/reportlinks/${linkId}`, 'DELETE');
}

export async function getSurveyReportLinks(surveyId: string): Promise<PublicSurveyReportLink[]> {
    return await queryAuthWrapper(`${surveysPath}/${surveyId}/report/links`);
}

export async function getSurveyReportFromLinkId(siteId: string, linkId: string): Promise<SharedSurveyReport | null> {
    return await queryAuthWrapper(`${surveysPath}/reportlinks/${siteId}.${linkId}`);
}

export async function getSurveySegmentFilterAliases(surveyId: string): Promise<SurveySegmentFilterAlias[]> {
    return await queryAuthWrapper(`${surveysPath}/${surveyId}/report/filteraliases`);
}

export async function createSurveySegmentFilterAlias(
    surveyId: string,
    aliasId: string,
    name: string,
    segmentFilter: SurveySegment[][]
): Promise<CommandResult> {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/report/filteraliases`, 'POST',
        { aliasId, name, segmentFilter });
}

export async function updateSurveySegmentFilterAlias(
    surveyId: string,
    aliasId: string,
    name: string,
    segmentFilter: SurveySegment[][]
): Promise<CommandResult> {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/report/filteraliases/${aliasId}`, 'PUT',
        { name, segmentFilter });
}

export async function deleteSurveySegmentFilterAlias(surveyId: string, aliasId: string): Promise<CommandResult> {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/report/filteraliases/${aliasId}`, 'DELETE');
}

export const getSurveyByActivityId = async (activityId: string): Promise<Survey | null> => {
    const result = await queryAuthWrapper(`${surveysPath}/activity/${activityId}`);
    return result === null
        ? null
        : { ...result, start: new Date(result.start), end: new Date(result.end) };
}

export const createSurvey = async (id: string, activityId: string): Promise<CommandResult> =>
(await commandAuthWrapper(`${surveysPath}`, 'POST',
    { id, activityId, surveyType: 'WPA', title: 'Nyt spørgeskema', introduction: 'Her skal indsættes en introduktion til respondenten...' }));

export const updateSurvey = async (id: string, title: string, start: Date, end: Date): Promise<CommandResult> => {
    return await commandAuthWrapper(`${surveysPath}/${id}`, 'PUT',
        { title, start, end });
};
export const updateSurveyIntroduction = async (surveyId:string, text:string, languageCode:LanguageCode): Promise<CommandResult> => {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/introduction`, 'PUT',
        { text, languageCode });
};
export const setSurveyLanguage = async (id: string, languageCode: LanguageCode): Promise<CommandResult> => {
    return await commandAuthWrapper(`${surveysPath}/${id}/language`, 'PUT',
        { languageCode });
};

export const createSurveyTranslation = async (id: string, languageCode: LanguageCode): Promise<CommandResult> => {
    return await commandAuthWrapper(`${surveysPath}/${id}/language`, 'POST',
        { languageCode });
};

export const deleteSurveyTranslation = async (id: string, languageCode: LanguageCode): Promise<CommandResult> => {
    return await commandAuthWrapper(`${surveysPath}/${id}/language`, 'DELETE',
        { languageCode });
};

export const deleteAllSurveyAnswers = async (surveyId: string): Promise<CommandResult> => {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/answers`, 'DELETE');
};

export async function createSurveyTheme(surveyId: string, theme: string): Promise<CommandResult> {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/themes`, 'POST',
        { name: theme });
}

export async function updateSurveyTheme(surveyId: string, themeIndex: number, newThemeName: string): Promise<CommandResult> {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/themes/${themeIndex}`, 'PUT',
        { newThemeName });
}

export async function updateSurveyThemeText(surveyId: string, themeIndex: number, languageCode:LanguageCode, text:string): Promise<CommandResult> {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/themes/${themeIndex}/text`, 'PUT',
        { languageCode, text });
}

export async function moveSurveyTheme(surveyId: string, fromThemeIndex: number, toThemeIndex: number): Promise<CommandResult> {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/themes/${fromThemeIndex}/move`, 'PUT',
        { toThemeIndex });
}

export async function deleteSurveyTheme(surveyId: string, themeIndex: number): Promise<CommandResult> {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/themes/${themeIndex}`, 'DELETE');
}

export const createSurveyQuestion = async (surveyId: string, themeIndex: number, indicators: string[], title: string, canComment:boolean, config: SurveyQuestionConfig, conditionalDisplay?: { triggerValues:string[] }): Promise<CommandResult> =>
(await commandAuthWrapper(`${surveysPath}/${surveyId}/themes/${themeIndex}/questions`, 'POST',
    { indicators, title, canComment, config, conditionalDisplay }));

export const updateSurveyQuestion = async (surveyId: string, themeIndex: number, questionIndex: number, indicators: string[], title: string, translations:LocalizedSurveyText[] | null, canComment:boolean, config:SurveyQuestionConfig, conditionalDisplay?: { triggerValues:string[] }): Promise<CommandResult> =>
(await commandAuthWrapper(`${surveysPath}/${surveyId}/themes/${themeIndex}/questions/${questionIndex}`, 'PUT',
    { indicators, title, translations, canComment, config, conditionalDisplay }));

// export async function updateSurveyQuestionText(surveyId:string, themeIndex:number, questionIndex:number, languageCode:LanguageCode, text:string): Promise<CommandResult> {
//     return await commandAuthWrapper(`${surveysPath}/${surveyId}/themes/${themeIndex}/questions/${questionIndex}/text`, 'PUT',
//         { languageCode, text });
// }


export const invertSurveyQuestion = async (surveyId: string, themeIndex: number, questionIndex: number, invert: boolean): Promise<CommandResult> =>
(await commandAuthWrapper(`${surveysPath}/${surveyId}/themes/${themeIndex}/questions/${questionIndex}/invert`, 'PUT',
    { invert }));

export async function moveSurveyQuestion(surveyId: string, fromThemeIndex: number, fromQuestionIndex: number, toThemeIndex: number, toQuestionIndex: number): Promise<CommandResult> {
    return await commandAuthWrapper(`${surveysPath}/${surveyId}/themes/${fromThemeIndex}/questions/${fromQuestionIndex}/move`, 'PUT',
        { toThemeIndex, toQuestionIndex });
}

export const deleteSurveyQuestion = async (surveyId: string, themeIndex: number, questionIndex: number): Promise<CommandResult> =>
    await commandAuthWrapper(`${surveysPath}/${surveyId}/themes/${themeIndex}/questions/${questionIndex}`, 'DELETE');




export const loadSurveyTemplate = async (surveyId: string, templateId: string): Promise<CommandResult> =>
    await commandAuthWrapper(`${surveysPath}/${surveyId}/loadtemplate/${templateId}`, 'PUT');

export const getSurveyTemplates = async (): Promise<SurveyTemplateView[]> =>
    await queryAuthWrapper(`${surveysPath}/templates`);

export const createSurveyTemplate = async (surveyId: string, description: string): Promise<CommandResult> =>
    await commandAuthWrapper(`${surveysPath}/templates/${rng.getRandomId('local_')}`, 'POST',
        { surveyId, description });

export const deleteSurveyTemplate = async (surveyTemplateId:string): Promise<CommandResult> =>
    await commandAuthWrapper(`${surveysPath}/templates/${surveyTemplateId}`, 'DELETE');